import { Box, Footer, Loader, PageLayout } from '@bharatpe/bharat-ui';
import React, { useEffect, useState } from 'react';
import GenericLoader from 'components/organisms/GenericLoader';
import BankDetails from './BankDetails';
import {
	SET_SELECTED_SIM,
	SET_USER_UPI_BANK_LIST,
	useDeviceSimList,
	useOnBoardingRedirection,
	useSelectedSim,
	useUpiData,
	useUserUpiBankList,
} from 'Slices';
import { fetchDeviceBindedNumber, TriggerNativeApiWrapper, userBankAccountSave } from 'utils/nativeUpiHandler';
import { UpiAccountTypes } from 'constants/upiConstants';
import { IUserBankData } from 'types';
import { useDispatch } from 'react-redux';
import history from 'utils/History';
import { SettlementAccountSlice, triggerSettlementAccountApi, useSettlementAccount } from 'Slices/GetSettlementAccount';
import { registerSlice } from 'store';
import AlertModal from '../Modals/AlertModal';
import CardIcon from 'assets/icons/Autopay/card.svg';
import AppHeader from 'components/atoms/AppHeader';
import { useMerchantInfo } from 'Slices/MercantInfoSlice';
import { globalId } from 'index';
import Toasty from 'components/atoms/Toasty';
import SkeletonUI from '@bharatpe/bharat-ui/web/SkeletonUI';
import UpiPoweredBy from 'components/atoms/UpiPoweredBy';
import './styles.scss';
import { isEmpty } from 'utils/Utils';
import { API_URL } from 'Services/CONSTANT';
import { customSubScribeBackButton, getURLParameter } from 'utils/nativeSupport';
import { ONBOARDING_REDIRECTION_PAGE, UPI_ERROR_CODE } from 'utils/CONSTANT';
import { triggerCustomerSettlementAccountApi, useCustomerSettlementAccount } from 'Slices/GetCustomerSettlementAccount';
import { deviceBindingRedirectionHandle } from 'utils/deviceBindingHelper';
import { sendEvent } from 'utils/Sentry';
import { logAnalyticsEvent } from 'utils/AnalyticsEvent';
import { useLanguageConfig } from 'Slices/LanguageConfigSlice';

registerSlice(SettlementAccountSlice);

const BankSelection = () => {
	const dispatch = useDispatch();
	const [bankName, setBankName] = useState<string>();
	const [loading, setLoading] = useState<boolean>(false);
	const [showDebitCardModal, setShowDebitCardModal] = useState<boolean>(false);
	const routeState: any = history?.location?.state;
	const settlementAccount = useSettlementAccount();
	const customerSettlementAccount = useCustomerSettlementAccount();

	const merchantInfo = useMerchantInfo();
	const useBankList = useUserUpiBankList();
	const sim = useSelectedSim();
	const deviceSimList = useDeviceSimList();
	const hist: any = history.location.state;
	const selectedSim = hist?.selectedSim;
	const [bankListLoading, setBankListLoading] = useState(true);
	const onBoardingRedirection = useOnBoardingRedirection();
	const upiData = useUpiData();
	const { user_banks } = upiData?.upiProfileDetails || {};
	const { languageData } = useLanguageConfig();

	useEffect(() => {
		logAnalyticsEvent({
			trigger_point: 'Page View',
			Product: 'AutoPayUPI',
			Flow: 'SetupUPI',
			Screen: 'BindingSuccess',
			Module: 'SuccessScreen',
			Action: 'Viewed',
		});
	}, []);
	useEffect(() => {
		switch (onBoardingRedirection.source) {
			case ONBOARDING_REDIRECTION_PAGE.PROFILE:
				if (isEmpty(customerSettlementAccount?.data)) dispatch(triggerCustomerSettlementAccountApi());

				break;
			case ONBOARDING_REDIRECTION_PAGE.MANDATE:
				if (isEmpty(settlementAccount?.data)) dispatch(triggerSettlementAccountApi());
				break;
		}
	}, [dispatch]);

	const backEventAction = () => {
		logAnalyticsEvent({
			trigger_point: `Clicks on back button`,
			Product: 'AutoPayUPI',
			Flow: 'SetupUPI',
			Screen: 'BindingSuccess',
			Element: 'back',
			Action: 'Tapped',
			source: 'appHeader',
		});
	};

	let accountType: any = UpiAccountTypes.DEBIT;

	if (routeState?.routeType) {
		accountType = routeState?.routeType;
	}

	const handleDeviceBindingError = async () => {
		setBankListLoading(true);
		const simCount = Object.keys(deviceSimList).length;
		if (isEmpty(sim) && simCount != 1) {
			setBankListLoading(false);
			if (!isEmpty(selectedSim)) {
				dispatch(SET_SELECTED_SIM(selectedSim?.phone));
			} else {
				const deviceBindedMobile = await fetchDeviceBindedNumber();
				dispatch(SET_SELECTED_SIM((deviceBindedMobile as any)?.mobile?.replace(/[".]/g, '')));
			}
			history.push({
				pathname: '/upi/sim-failure',
			});
		} else {
			setBankListLoading(false);
			history.push({
				pathname: '/upi/sim-binding-failure',
			});
		}
	};

	const getBankList = async (providerId: string) => {
		try {
			const getUserBank: any = await TriggerNativeApiWrapper(
				`${API_URL.upi_settlement_account}`,
				'POST',
				{
					application_id: globalId,
					...(!isEmpty(user_banks) && user_banks?.length != 0 && { vpa: user_banks[0]?.va }),
				},
				{},
			);
			if (getUserBank?.accountList?.length <= 0) {
				return;
			}

			if (getUserBank) {
				const updatedBankData = getUserBank?.accountList?.map((bank: IUserBankData) => ({
					...bank,
					bank_provider_name: bank.bankName,
				}));
				dispatch(
					SET_USER_UPI_BANK_LIST({
						bankData: updatedBankData,
						vpa: getUserBank?.vpa || '',
						bankId: getUserBank?.selectedBankId || '',
						accountProviderId: getUserBank?.accountProviderId || '',
						userReferenceId: getUserBank?.userReferenceId || '',
					}),
				);
			}
			setLoading(false);
			setBankListLoading(false);
		} catch (err: any) {
			setLoading(false);
			setBankListLoading(false);
			if (
				err?.error === 'Settlement Bank Account is not matching with provided list' ||
				err?.error === 'No account mapped from NPCI'
			) {
				handleDeviceBindingError();
			} else {
				Toasty.error('Something went wrong!!!');
				sendEvent(err);
				deviceBindingRedirectionHandle(true);
			}
		}
	};

	useEffect(() => {
		switch (onBoardingRedirection.source) {
			case ONBOARDING_REDIRECTION_PAGE.PROFILE:
				if (!isEmpty(customerSettlementAccount?.data?.data?.providerId)) {
					getBankList(customerSettlementAccount?.data?.data?.providerId);
				}

				break;
			case ONBOARDING_REDIRECTION_PAGE.MANDATE:
				if (!isEmpty(settlementAccount?.data?.data?.providerId)) getBankList(settlementAccount?.data?.data?.providerId);
				break;
		}
	}, [settlementAccount, customerSettlementAccount]);

	const handleNext = async (accNo: string, bankName: string, mbeba: string) => {
		logAnalyticsEvent({
			trigger_point: `Clicks on Continue`,
			Product: 'AutoPayUPI',
			Flow: 'SetupUPI',
			Screen: 'BindingSuccess',
			Module: 'Continue',
			Element: 'CTA',
			Action: 'Tapped',
			ctaType: 'continue',
		});
		logAnalyticsEvent({
			trigger_point: `Linking UPI ID Screen`,
			Product: 'AutoPayUPI',
			Flow: 'SetupUPI',
			Screen: 'LinkUPI',
			Module: 'Page',
			Action: 'Viewed',
		});
		setLoading(true);
		setBankName(bankName);
		const filteredBankData: any = useBankList?.bankData?.find((bank: any) => String(bank.account) === String(accNo));
		try {
			if (mbeba == 'Y') {
				const saveBankAccount: any = await userBankAccountSave(
					filteredBankData,
					useBankList?.vpa,
					String(useBankList?.accountProviderId),
					useBankList?.userReferenceId,
				);
				setLoading(false);
				if (saveBankAccount) {
					history.push({ pathname: '/upi/upi-created', state: filteredBankData });
				}
			} else {
				setLoading(false);
				setShowDebitCardModal(true);
			}
		} catch (err: any) {
			if (UPI_ERROR_CODE.includes(err?.errorCode)) Toasty.error(err?.error);
			else Toasty.error('Something went wrong !!!');
			setLoading(false);
			sendEvent(err);
		}
	};

	const handleSaveDebitDetails = async (accNo: string, bankName: string) => {
		setLoading(true);
		setBankName(bankName);
		const filteredBankData: any = useBankList?.bankData?.find((bank: any) => String(bank.account) === String(accNo));
		try {
			const saveBankAccount: any = await userBankAccountSave(
				filteredBankData,
				useBankList?.vpa,
				String(useBankList?.accountProviderId),
				useBankList?.userReferenceId,
			);
			setLoading(false);
			setShowDebitCardModal(false);
			if (saveBankAccount) {
				history.push({ pathname: '/upi/upi-created', state: filteredBankData });
			}
		} catch (err: any) {
			setLoading(false);
			if (UPI_ERROR_CODE.includes(err?.errorCode)) Toasty.error(err?.error);
			else Toasty.error('Something went wrong !!!');
			sendEvent(err);
		}
	};

	useEffect(() => {
		customSubScribeBackButton(() => {
			handleBack();
		});
	}, [history]);

	const handleBack = () => {
		deviceBindingRedirectionHandle(true);
	};

	if (loading) return <GenericLoader primaryText={languageData.WE_ARE_LINKING} secondryText={languageData.UPI_ID} />;

	return bankListLoading ? (
		<Loader loading />
	) : (
		<PageLayout
			className="pb18 white-background"
			footerContent={
				<Footer
					primaryActionText={languageData.CONTINUE}
					primaryAction={() =>
						handleNext(
							useBankList?.bankData[0]?.account,
							useBankList?.bankData[0]?.bank_provider_name,
							useBankList?.bankData[0]?.mbeba,
						)
					}
				/>
			}>
			<>
				<div className="mt3">
					<AppHeader title={''} leftIconAction={handleBack} backEventAction={backEventAction} />
				</div>
				<div className="content-center  ">
					<p className="mt9 Bold400">{languageData.SELECT_BANK_ACCOUNT}</p>
				</div>
				{useBankList ? (
					<Box className="mx5">
						{useBankList?.bankData?.map((bank, index) => {
							return (
								<BankDetails
									showLoader={loading}
									key={index}
									bankDetails={bank}
									onClickHandler={() => handleNext(bank?.account, bank?.bank_provider_name, bank?.mbeba)}
								/>
							);
						})}
					</Box>
				) : (
					<div className="m4">
						<SkeletonUI
							variant="announcement-card"
							className="br2 mt5 my2"
							options={{ width: '80%', height: 30, variant: 'rect' }}
						/>
					</div>
				)}

				<AlertModal
					handleClick={() =>
						handleSaveDebitDetails(useBankList?.bankData[0]?.account, useBankList?.bankData[0]?.bank_provider_name)
					}
					title={languageData.SET_PIN_WITH_DEBIT_CARD}
					description={languageData.KINDLY_PROCEED_ONLY_IF_YOU_HAVE_THE_SAME_MOBILE_NUMBER_MOBILENUMBER_REGISTERED_WITH_DEBIT_CARD_KEEP_YOUR_DEBIT_CARD_WITH_YOU_TO_VARIFY_16_DIGIT_NUMBER.replace(
						'{0}',
						merchantInfo?.data?.mobile,
					)}
					buttonName={languageData.CONTINUE}
					icon={CardIcon}
					type={'info'}
					visible={showDebitCardModal}
					showCloseButton={false}
				/>
				<div className="upi-powered-with-margin">
					<UpiPoweredBy />
				</div>
			</>
		</PageLayout>
	);
};

export default BankSelection;
